import type {VoidFunctionComponent} from "react";
import React from "react";
import s from "./TextBoxDocumentation.module.scss";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import TextBox, {autoCompleteValues, inputModeValues} from "../../../components/common/textBox/TextBox";
import {getLoremIpsumText} from "../../../util/string-util";

export interface ITextBoxDocumentationProps {
}

export const TextBoxDocumentation: VoidFunctionComponent<ITextBoxDocumentationProps> = ({}) => {
  let propertyIndex = 0;

  const properties: IPropertyWrapper[] = [
    {
      isRequired: false,
      name: "value",
      element: <Property
        key={++propertyIndex}
        name={"value"}
        description={"Initial value that is displayed in the box."}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={"value = 'Sample text'"}>
            <TextBox
              onChange={(_value) => {}}
              value={"Sample text"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "autoCapitalize",
      element: <Property
        key={++propertyIndex}
        name={"autoCapitalize"}
        description={"Primarily a mobile feature. Affects how virtual keyboard chooses to capitalize sentences. If true, first letter of sentences will be capitalized automatically."}
        isRequired={false}
        type={"boolean"}
        defaultValue={'false'}
        examples={<>
          <Example title={"autoCapitalize = true"}>
            <TextBox
              autoCapitalize={true}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "autoComplete",
      element: <Property
        key={++propertyIndex}
        name={"autoComplete"}
        description={"If provided, the browser will attempt to supply a value for the text box."}
        isRequired={false}
        type={'"' + autoCompleteValues.join('" | "') + '"'}
        examples={<>
          <Example title={"autoComplete = 'cc-name'"}>
            <TextBox
              autoComplete={"cc-name"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "disabled",
      element: <Property
        key={++propertyIndex}
        name={"disabled"}
        description={"Determines whether or not the text box is disabled."}
        isRequired={false}
        type={"boolean"}
        examples={<>
          <Example title={"disabled = 'true'"}>
            <TextBox
              disabled={true}
              value={getLoremIpsumText(20)}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "errorMessages",
      element: <Property
        key={++propertyIndex}
        name={"errorMessages"}
        description={<>
          One or more error messages to display above the text box and below the label. Messages will be listed on one line separated by a space.<br />
          <span className={s.warning}> NOTE: errorMessages are only displayed if isErrorState is set to &apos;true&apos;.</span>
        </>}
        isRequired={false}
        type={"string[]"}
        examples={<>
          <Example title={"errorMessages set"}>
            <TextBox
              isErrorState={true}
              errorMessages={["This is an error message.", "This is another error message.", "This is a third error message."]}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "inputMode",
      element: <Property
        key={++propertyIndex}
        name={"inputMode"}
        description={"Can be used to tell the browser what kind of data is expected. Browser may then display an appropriate virtual keyboard."}
        isRequired={false}
        type={'"' + inputModeValues.join('" | "') + '"'}
        examples={<>
          <Example title={"inputMode = 'url'"}>
            <TextBox
              inputMode={"url"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "isEmail",
      element: <Property
        key={++propertyIndex}
        name={"isEmail"}
        description={"Sets the type of the input to email. Field is automatically validated before the form it is in can be submitted. ':valid' and ':invalid' CSS pseudo-classes are automatically applied as appropriate to visually denote whether the current value is a valid email address or not."}
        isRequired={false}
        type={'Boolean'}
        examples={<>
          <Example title={"isEmail = 'true'"}>
            <TextBox
              isEmail={true}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "isErrorState",
      element: <Property
        key={++propertyIndex}
        name={"isErrorState"}
        description={"Determines whether or not errorMessages are visible. If errorMessages array has length of zero, nothing will be shown even if isErrorState is true."}
        isRequired={false}
        type={'Boolean'}
        defaultValue={'false'}
        examples={<>
          <Example title={"isErrorState = 'true', errorMessages has 1 message"}>
            <TextBox
              isErrorState={true}
              errorMessages={["This is an error message."]}
            />
          </Example>
          <br />
          <br />
          <Example title={"isErrorState = 'true', errorMessages has 0 messages"}>
            <TextBox
              isErrorState={true}
              errorMessages={[]}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "isPassword",
      element: <Property
        key={++propertyIndex}
        name={"isPassword"}
        description={"Sets the type of the input to password. Value is initially masked. Icon to view / mask the value appears."}
        isRequired={false}
        type={'Boolean'}
        defaultValue={'false'}
        examples={<>
          <Example title={"isPassword = 'true'"}>
            <TextBox
              isPassword={true}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "isRequired",
      element: <Property
        key={++propertyIndex}
        name={"isRequired"}
        description={"Determines whether or not the value is required in a form."}
        isRequired={false}
        type={'Boolean'}
        defaultValue={'false'}
        examples={<>
          <Example title={"isRequired = 'true'"}>
            <TextBox
              isRequired={true}
            />
          </Example>
          <br />
          <br />
          <Example title={"isRequired = 'false'"}>
            <TextBox
              isRequired={false}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "label",
      element: <Property
        key={++propertyIndex}
        name={"label"}
        description={"If provided, the text will appear above the text box."}
        isRequired={false}
        type={'String'}
        examples={<>
          <Example title={"label = 'My label'"}>
            <TextBox
              label={"My label"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "note",
      element: <Property
        key={++propertyIndex}
        name={"note"}
        description={<>
          If provided, the text will appear above the text box and below the label.<br />
          <span className={s.warning}> NOTE: note will only appear when errorMessages are not being displayed.</span>
        </>}
        isRequired={false}
        type={'String'}
        examples={<>
          <Example title={"note = 'My note'"}>
            <TextBox
              label={"My label"}
              note={"My note"}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "placeHolder",
      element: <Property
        key={++propertyIndex}
        name={"placeHolder"}
        description={"A hint that appears in the text box when it is empty."}
        isRequired={false}
        type={'String'}
        examples={<>
          <Example title={"placeHolder = 'Enter your first name...'"}>
            <TextBox
              placeHolder={"Enter your first name..."}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "onChange",
      element: <Property
        key={++propertyIndex}
        name={"onChange"}
        description={"Callback fired when the value in the text box changes. New value is passed as an argument to the callback."}
        isRequired={false}
        type={'(newValue: string) => void'}
        examples={<>
          <Example title={"onChange = '(value) => console.log(value)"}>
            <TextBox
              onChange={(value) => console.log(value)}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "onBlur",
      element: <Property
        key={++propertyIndex}
        name={"onBlur"}
        description={"Callback fired when the text box loses focus."}
        isRequired={false}
        type={'() => void'}
        examples={<>
          <Example title={"onBlur = '() => console.log(\"blurring\")"}>
            <TextBox
              onBlur={() => console.log("blurring")}
            />
          </Example>
        </>}
      />
    },

  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"TextBox"}
      description={"A box a user can type information into."}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}
  </div>;
}
